import React from 'react';
import { Translate, useTranslate, TranslateHTML } from 'core/translate';
import FeatureFilter, {
  FeatureFilterCheckbox,
  FeatureFilterRadio,
  FeatureFilterSection,
  FilterCount,
} from '../../../../../common/featureFilter/FeatureFilter';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalFooterButtons,
  ModalFooterMessage,
  ModalFooterPrimaryButton,
  ModalHeader,
} from '../../../../../components/modal/Modal';
import getDriveTypeDescription from '../../../../../utils/getDriveTypeDescription';
import { combineClassNames } from 'core/css';
import { flatMap, get } from 'lodash';
import { Link } from 'react-router-dom';
import { SvgIcon } from '../../../../../../core/icons';
import { SimpleOptionInfo } from '../../../../../common/options/SimpleOptionCard';
import { Route, Routes, useNavigate } from 'react-router';
import { SummaryInner as PopConInner } from '../../../../summary/Summary';
import useLargeBreakpoint from '../../../../../hooks/useLargeBreakpoint';
import SimpleOptionDetails from '../../../../../common/optionDetails/simpleOptionDetails/SimpleOptionDetails';
import CapabilitiesCompareModal from '../../capabilitiesCompareModal/CapabilitiesCompareModal';

export const PopConListItem = ({
  children,
  option,
  onDetails,
  analyticsKey,
}) => {
  const infoIcon = (
    <span className="popcon-filter__list-item__info-icon">
      <SimpleOptionInfo
        option={option}
        onDetails={onDetails}
        analyticsKey={analyticsKey}
      />
    </span>
  );
  const childrenWithProps = React.Children.map(children, (child) => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { infoIcon });
    }
    return child;
  });

  return <div className="popcon-filter__list-item">{childrenWithProps}</div>;
};

export const FilterContent = ({
  options,
  onDriveChange,
  onEngineChange,
  selections,
  engines,
  keyFeatures,
  onFeatureChange,
  navigate,
  filteredGroups,
  analyticsKey,
}) => {
  const translate = useTranslate();

  const filteredTrimOptionsFull = flatMap(
    filteredGroups.filter((trim) => !trim.displayOnlyLabel),
    'options'
  );

  const availableStyleIds = flatMap(filteredTrimOptionsFull, 'styleId');

  const engineList = [
    {
      description: translate('no.engine.preference.v3'),
      id: '',
    },
    ...engines,
  ];

  const analyticsBase = `${analyticsKey}:filters`;

  const compareEngineLink = (
    <Link
      to={'capabilities'}
      className={combineClassNames(
        'popcon-filter__section-title__link',
        'stat-text-link'
      )}
      data-dtm={analyticsBase}>
      <SvgIcon name="compare" />
      <TranslateHTML
        className="popcon-filter__section-title__link-label"
        value="compare.all.v3"
      />
    </Link>
  );

  const keyFeaturesDisclosure = translate(
    'feature.filter.key.features.disclosure.v3'
  );

  const popConFiltersDisclosure = translate(
    'feature.filter.pop.con.filters.disclaimer.v3'
  );

  const onFeatureDetails = (option) => {
    navigate(
      `feature/${option.code}?infoOnly=${option.checked || option.disabled}`
    );
  };
  const onEngineDetails = (option) => {
    const optionSelected = selections.engine === option.id;

    navigate(
      `engine/${option.id}?infoOnly=${optionSelected || option.disabled}`
    );
  };

  const featureList = [
    ...keyFeatures.map((feature) => {
      return {
        ...feature,
        checked: selections.selectedKeyFeatures.includes(feature.code),
        disabled:
          feature.styleIds.filter((id) => availableStyleIds.includes(id))
            .length < 1,
      };
    }),
  ];

  const isLarge = useLargeBreakpoint(true);

  const driveTypeAnalyticsKey = `${analyticsBase}:drive type`;
  const engineAnalyticsKey = `${analyticsBase}:engine`;
  const keyFeaturesAnalyticsKey = `${analyticsBase}:key features`;

  return (
    <>
      <div className="popcon-filter__disclaimer">
        <SvgIcon
          name="assist"
          className={combineClassNames(
            'popcon-filter__disclaimer-icon',
            'button__icon--left'
          )}
        />
        <span dangerouslySetInnerHTML={{ __html: popConFiltersDisclosure }} />
      </div>
      <div className="popcon-filters__container">
        <PopConInner
          condition={isLarge}
          className="popcon-filters__inner-container-column">
          {options && options.length > 1 && (
            <FeatureFilterSection
              onChange={(e) => onDriveChange(e)}
              title={translate('component.common.select.drive.type')}
              modifier={'drive-type'}>
              {options.map(({ driveType, rearWheelType, id, label }) => (
                <FeatureFilterRadio
                  key={id}
                  name="driveoptions"
                  value={id}
                  checked={
                    selections.driveType === driveType &&
                    selections.rearWheelType === rearWheelType
                  }
                  primaryDescription={label}
                  secondaryDescription={
                    rearWheelType
                      ? rearWheelType
                      : getDriveTypeDescription(driveType, translate)
                  }
                  analyticsKey={driveTypeAnalyticsKey}></FeatureFilterRadio>
              ))}
            </FeatureFilterSection>
          )}
          {engines && engines.length > 1 && (
            <FeatureFilterSection
              onChange={(e) => onEngineChange(e.target.value)}
              title={translate('component.common.select.engine')}
              modifier={'engine'}
              link={compareEngineLink}>
              {engineList.map((engine) => (
                <PopConListItem
                  key={engine.id ? engine.id : 'NA'}
                  option={engine}
                  onDetails={onEngineDetails}
                  analyticsKey={analyticsBase}>
                  <FeatureFilterRadio
                    name="engine"
                    value={engine.id || ''}
                    disabled={engine.disabled}
                    checked={selections.engine === engine.id}
                    primaryDescription={engine.description}
                    analyticsKey={engineAnalyticsKey}
                  />
                </PopConListItem>
              ))}
            </FeatureFilterSection>
          )}
        </PopConInner>
        <div
          className={combineClassNames(
            'popcon-filter__container--key-features'
          )}>
          {keyFeatures && keyFeatures.length > 0 && (
            <FeatureFilterSection
              title={translate('pop.con.filter.features.v3')}
              modifier={'key-features'}
              disclosure={keyFeaturesDisclosure}>
              {featureList.map((feature) => (
                <PopConListItem
                  key={feature.code}
                  option={feature}
                  onDetails={onFeatureDetails}
                  analyticsKey={analyticsBase}>
                  <FeatureFilterCheckbox
                    disabled={feature.disabled}
                    onChange={() => onFeatureChange(feature)}
                    name="keyfeatures"
                    value={feature.code}
                    checked={feature.checked}
                    primaryDescription={feature.description}
                    analyticsKey={keyFeaturesAnalyticsKey}
                  />
                </PopConListItem>
              ))}
            </FeatureFilterSection>
          )}
        </div>
      </div>
    </>
  );
};

export const MobilePopConFilterModal = ({
  children,
  onToggle,
  filterCountText,
  displayFooter,
  isUpdating,
  onFilterReset,
  displayReset,
  resetAnalytics,
}) => {
  const translate = useTranslate();

  const scrollHeaderTop = () => {
    const elStepHeader = document.querySelector('.step-header');
    const trueHeaderDistanceToTop =
      window.pageYOffset + elStepHeader.getBoundingClientRect().bottom;

    window.scrollTo({
      top: trueHeaderDistanceToTop,
      left: 0,
      behavior: 'smooth',
    });
  };

  const onSelect = () => {
    onToggle();
    scrollHeaderTop();
  };

  return (
    <Modal
      isOpen={true}
      contentLabel={''}
      onRequestClose={onToggle}
      overlayClassName="reveal-overlay--background">
      <ModalHeader
        text={translate('feature.filter.title.v3')}
        analyticsKey={''}
        onClose={onToggle}
      />
      <ModalBody
        innerClassName={`pop-con-modal__inner${
          isUpdating ? ' pop-con-modal__inner--updating' : ''
        }`}>
        {children}
      </ModalBody>
      {displayFooter ? (
        <ModalFooter className="pop-con-filter__modal-footer">
          <ModalFooterMessage className="pop-con-filter__modal-footer-count">
          <FilterCount
            isUpdating={isUpdating}
            filterCountText={filterCountText}
            displayReset={displayReset}
            handleReset={onFilterReset}
            analyticsKey={resetAnalytics}
          />
          </ModalFooterMessage>
          <ModalFooterButtons classModifier="pop-con">
            <ModalFooterPrimaryButton
              className={combineClassNames(
                'pop-con__select-button',
                'stat-button-link'
              )}
              onSelect={onSelect}
              data-dtm={''}>
              <Translate value="feature.filter.view.results.v3" />
            </ModalFooterPrimaryButton>
          </ModalFooterButtons>
        </ModalFooter>
      ) : (
        <></>
      )}
    </Modal>
  );
};

const PopConFilter = (props) => {
  const {
    isAtLeastLarge,
    keyFeatures,
    engines,
    onFeatureChange,
    onEngineChange,
    filteredGroups,
    selections,
    options,
    onFilterReset,
    displayReset,
    isUpdating,
    features,
    analyticsKey,
  } = props;
  const translate = useTranslate();
  const navigate = useNavigate();

  const { engine, selectedKeyFeatures, driveType, rearWheelType } = selections;

  const scrollAccordionTop = () => {
    const elStepNav = document.querySelector('.step-nav');
    const elAccordion = document.querySelector('.accordion');

    const trueAccordionDistanceToTop =
      window.pageYOffset + elAccordion.getBoundingClientRect().top;

    window.scrollTo({
      top:
        trueAccordionDistanceToTop - elStepNav.getBoundingClientRect().height,
      left: 0,
      behavior: 'smooth',
    });
  };

  // TODO: I believe we should be able to combine onCapabilitiesClose and onDetailsClose to onModalClose.
  // I want to leave it separates for now in case we need to do something onCapabilitiesClose.
  const onCapabilitiesClose = () => {
    navigate('');
  };

  const onDetailsClose = () => {
    navigate('.');
  };

  const filterCount = filteredGroups.length;
  const filterCountLabel =
    filterCount === 1
      ? 'feature.filter.trim.count.v3'
      : 'feature.filter.trim.count.plural.v3';
  const filterCountText = translate(filterCountLabel, {
    numberOfTrims: filterCount,
  });

  // TODO: Render HTML not text for pills.
  const calculatePills = () => {
    let listOPills = [];

    if (options.length > 1 && selections.driveType) {
      const dt = options.find(
        (opt) =>
          driveType === opt.driveType && rearWheelType === opt.rearWheelType
      );

      if (dt) {
        listOPills = [
          `${dt.label}${dt.rearWheelType ? ' ' + dt.rearWheelType : ''}`,
        ];
      }
    }

    if (engine) {
      const eng = engines.find(({ id }) => engine === id);
      listOPills = [...listOPills, eng?.description];
    }

    if (selectedKeyFeatures.length > 0) {
      const keyFeatureDescs = selectedKeyFeatures
        .map((kF) =>
          get(
            keyFeatures.find((f) => f.code === kF),
            'description',
            null
          )
        )
        .filter((n) => n);
      listOPills = [...listOPills, ...keyFeatureDescs];
    }

    return listOPills;
  };

  const onEngineSelect = (option) => {
    onEngineChange(option.id);
    navigate('');
  };

  const onFeatureSelect = (option) => {
    onFeatureChange(option);
    navigate('');
  };

  const pills = calculatePills();

  const resetAnalytics = analyticsKey + ':filters';

  return (
    <>
      <FeatureFilter
        pillsList={pills}
        openDefault={false}
        isUpdating={isUpdating}
        responsivePills
        dialog={
          <MobilePopConFilterModal
            displayFooter={displayReset}
            filterCountText={filterCountText}
            isUpdating={isUpdating}
            onFilterReset={onFilterReset}
            displayReset={displayReset}
            resetAnalytics={resetAnalytics}
          >
            <FilterContent {...props} navigate={navigate} />
          </MobilePopConFilterModal>
        }
        useMobileDialog={!isAtLeastLarge}
        onToggle={isAtLeastLarge ? scrollAccordionTop : () => {}}
        analyticsKey={analyticsKey}>
        <FilterContent {...props} navigate={navigate} />
      </FeatureFilter>
      <FilterCount
        isUpdating={isUpdating}
        filterCountText={filterCountText}
        displayReset={displayReset}
        handleReset={onFilterReset}
        analyticsKey={resetAnalytics}
      />
      <Routes>
        <Route
          path="/feature/:id"
          element={
            <SimpleOptionDetails
              options={keyFeatures}
              onClose={onDetailsClose}
              onSelect={onFeatureSelect}
            />
          }
        />
        <Route
          path="/engine/:id"
          element={
            <SimpleOptionDetails
              options={engines}
              onClose={onDetailsClose}
              isEngine
              onSelect={onEngineSelect}
            />
          }
        />
        <Route
          path="/capabilities"
          element={
            <CapabilitiesCompareModal
              onClose={onCapabilitiesClose}
              onSelect={onEngineSelect}
              filteredEngines={engines}
              filteredGroups={filteredGroups}
              featureSpecs={features}
              trimFiltersSelections={selections}
            />
          }
        />
      </Routes>
    </>
  );
};

export default PopConFilter;
